import { useStanQuery } from '@/queries';
import { appointmentsApi } from '@/lib/api/appointments';
import { getLocalTimezone, QUERY_KEYS } from '@/lib';
import { endOfMonth, format, startOfMonth } from 'date-fns';
import type { AvailabilityResponse } from '@/lib/api/appointments/availabilityTypes';

interface UseAvailabilityParams {
    bookingId: string;
    month: Date; // The month to fetch availability for
    enabled?: boolean;
}

/**
 * Query for fetching availability for a specific month
 * Used in reschedule modal to show which dates/times are available
 */
export function useAvailability({ bookingId, month, enabled = true }: UseAvailabilityParams) {
    const startDate = format(startOfMonth(month), 'yyyy-MM-dd');
    const endDate = format(endOfMonth(month), 'yyyy-MM-dd');
    const timezone = getLocalTimezone();

    return useStanQuery<AvailabilityResponse>({
        queryKey: [QUERY_KEYS.AVAILABILITY, bookingId, startDate, endDate, timezone],
        queryFn: async () => {
            return await appointmentsApi.getAvailability(bookingId, startDate, endDate, timezone);
        },
        enabled: enabled && !!bookingId,
        staleTime: 5 * 60 * 1000, // 5 minutes
        placeholderData: previousData => previousData, // Keep previous data while fetching
        meta: { error: { sentryMessage: '[APPOINTMENTS] failed-to-fetch-availability' } },
    });
}
